import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../components/card/Card';
import H from '../components/texts/h/H';
import Button from '../components/button/Button';
import Icon from '../components/icon/Icon';
import Table from '../components/table/table';
import TableSkeleton from '../components/TableSkeleton/TableSkeleton';
import NewPartnerModal from '../components/modals/NewPartnerModal';
import { getPartners, getPartnerFormMetadata } from '../services/GuaranteesService';
import { ITableData } from '../types/interfaces';
import { routes } from '../routes/routePaths';
import { useFiscalIdConfig } from '../hooks/useFiscalIdConfig';

interface Partner {
  id: number;
  name: string;
  cuit: string;
  share_quantity: number;
  hierarchy_level_id: number;
  enabled: boolean;
  created_at: string;
  updated_at: string;
  ceded_share_quantity?: number;
  available_share_quantity?: number;
}

interface HierarchyLevel {
  id: number;
  name: string;
}

const Partners = () => {
  const navigate = useNavigate();
  const fiscalId = useFiscalIdConfig();
  const [partners, setPartners] = useState<Partner[]>([]);
  const [hierarchyLevels, setHierarchyLevels] = useState<HierarchyLevel[]>([]);
  const [tableData, setTableData] = useState<ITableData>();
  const [page, setPage] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editingPartner, setEditingPartner] = useState<Partner | null>(null);

  const lastRequest = useRef(0);

  useEffect(() => {
    fetchMetadata();
    fetchPartners();
  }, [page]);

  const fetchMetadata = async () => {
    try {
      const response = await getPartnerFormMetadata();
      if (response.success && 'data' in response) {
        setHierarchyLevels(response.data.hierarchy_levels || []);
      }
    } catch (error) {
      console.error('Error fetching metadata:', error);
    }
  };

  const fetchPartners = async () => {
    setIsLoading(true);
    const requestId = Date.now();
    lastRequest.current = requestId;

    try {
      const response = await getPartners();
      
      if (lastRequest.current !== requestId) return;
      
      if (response.success && 'data' in response) {
        setPartners(response.data);
        // Mock table data for pagination (adjust if backend provides pagination)
        setTableData({
          size: response.data.length,
          page: 0,
          totalElements: response.data.length,
          totalPages: 1,
        });
      } else {
        setPartners([]);
      }
    } catch (error) {
      console.error('Error fetching partners:', error);
      setPartners([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePage = (data: number) => {
    if (data < 0 || data === tableData?.totalPages) {
      return;
    }
    setPage(data);
  };

  const handleGoBack = () => {
    navigate(routes.settings);
  };

  const handleOpenModal = () => {
    setEditingPartner(null);
    setIsModalOpen(true);
  };

  const handleEditPartner = (partner: Partner) => {
    setEditingPartner(partner);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingPartner(null);
  };

  const handlePartnerCreated = () => {
    fetchPartners();
  };

  const columns = [
    {
      name: 'Nombre',
      selector: (row: Partner) => row.name,
      cell: (row: Partner) => (
        <Card>
          {row.name}
        </Card>
      ),
      sortable: true,
    },
    {
      name: fiscalId.label,
      selector: (row: Partner) => row.cuit,
      cell: (row: Partner) => (
        <Card>
          {fiscalId.format(row.cuit)}
        </Card>
      ),
      sortable: true,
    },
    {
      name: 'Acciones',
      selector: (row: Partner) => row.share_quantity,
      cell: (row: Partner) => (
        <Card>
          {row.share_quantity ?? 0}
        </Card>
      ),
      sortable: true,
    },
    {
      name: 'Acciones cedidas',
      selector: (row: Partner) => row.ceded_share_quantity ?? 0,
      cell: (row: Partner) => (
        <Card>
          {row.ceded_share_quantity ?? 0}
        </Card>
      ),
      sortable: true,
    },
    {
      name: 'Acciones disponibles',
      selector: (row: Partner) => row.available_share_quantity ?? 0,
      cell: (row: Partner) => (
        <Card>
          {row.available_share_quantity ?? 0}
        </Card>
      ),
      sortable: true,
    },
    {
      name: 'Rol jerárquico',
      selector: (row: Partner) => row.hierarchy_level_id,
      cell: (row: Partner) => {
        const hierarchyLevel = hierarchyLevels.find(level => level.id === row.hierarchy_level_id);
        return (
          <Card>
            {hierarchyLevel ? hierarchyLevel.name : row.hierarchy_level_id}
          </Card>
        );
      },
      sortable: true,
    },
    {
      name: '',
      cell: (row: Partner) => (
        <Card>
          <Icon
            name='edit_outline'
            width={20}
            height={20}
            cursor='pointer'
            variant='info'
            onClick={() => handleEditPartner(row)}
            aria-label={`Editar socio ${row.name}`}
          />
        </Card>
      ),
      center: true,
      width: '56px',
    },
  ];

  return (
    <Card
      variant='neutral'
      modifier='inverted'
      spacing='4'
      height='-webkit-fill-available'
      width='85vw'
    >
      <Card display='flex' justifyContent='space-between' alignItems='center' margin={{ bottom: '3' }}>
        <Card display='flex' alignItems='center' gap='8px'>
          <Button 
            icon='arrow_left_outline'
            variant='tertiary'
            size='sm'
            iconAlign='center'
            onClick={handleGoBack}
            aria-label="Volver a configuración"
          />
          <H withMargin={false} type='1' weight='bold' modifier='strong'>
            Socios
          </H>
        </Card>
        
        <Button variant='primary' onClick={handleOpenModal}>
          Nuevo socio
        </Button>
      </Card>

      {isLoading ? (
        <Card margin={{ top: '4' }}>
          <TableSkeleton rows={10} columns={columns.length} />
        </Card>
      ) : partners.length > 0 ? (
        <Card margin={{ top: '4' }} justifyContent='center' alignItems='center'>
          <Table
            columns={columns}
            data={partners}
            tableData={tableData}
            handleChangePage={handleChangePage}
          />
        </Card>
      ) : (
        <Card
          display='grid'
          justifyContent='center'
          margin={{ top: '11', bottom: '11' }}
          width='53vw'
        >
          <Card display='flex' justifyContent='center' margin={{ bottom: '2' }}>
            <Icon name='folder_empty' width={80} height={80} variant='primary' />
          </Card>
          <H variant='primary' align='center' withMargin={false} weight='bold' type='3'>
            No hay socios registrados.
          </H>
        </Card>
      )}

      <NewPartnerModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSuccess={handlePartnerCreated}
        partnerToEdit={editingPartner}
      />
    </Card>
  );
};

export default Partners;
